// 카엘 걷기 층(kael_walk.glb, Mixamo standard_walk 제자리 리타깃 — qa/motion-20261103/walk-clip.md).
// 모바일 조이스틱을 덜 기울여 느리게 움직이면 달리기 클립을 느리게 돌리는 대신(슬로모션 달리기)
// 하체(골반·다리·발)만 걷기 클립과 섞는다. 상체·대검 소켓은 달리기 그대로(대검 휴대 자세 유지).
// 위상은 달리기와 같은 거리 위상을 쓰고, 왼발 앞 디딤 시점을 맞춰(phaseOffset) 두 클립의 다리가 서로 엇갈리지 않게 한다.
import * as THREE from 'three/webgpu';
import { clone } from 'three/addons/utils/SkeletonUtils.js';

/** 이 속도(m/s, 화면 기준) 이하는 걷기 하체 100%(상한 WALK_MAX) */
export const WALK_FULL = 2.2;
/** 이 속도 이상은 달리기 100%(키보드·조이스틱 끝까지 = 7m/s 는 언제나 달리기) */
export const RUN_FULL = 4.2;
/** 걷기 하체 비율 상한: 1이면 층 가중치가 무한대로 간다 */
export const WALK_MAX = 0.97;
/** 걷기 비율이 목표를 따라가는 속도(1/s). 조이스틱 흔들림에 다리가 떨지 않게 */
export const WALK_FOLLOW = 6;
/** 걷기 비율이 줄어들 때(빨라질 때) 따라가는 속도(1/s). 키보드 출발 첫 프레임의 낮은 보간 속도가 걷기로 오래 남지 않게 */
export const WALK_RELEASE = 30;

/** 디딤 판정 여유(m, 모델 단위): 발목 본 최저 높이 위 이 안이면 디딤 */
const STANCE_BAND = 0.08;

const LOWER = /(Hips|UpLeg|Leg|Foot|Toe)/;

export interface WalkLayer {
  /** 하체 트랙만 남긴 걷기 클립 */
  clip: THREE.AnimationClip;
  /** 걷기 한 바퀴 이동 거리(m, KAEL_SCALE 적용) */
  cycleM: number;
  /** 달리기 거리 위상(0..1) → 걷기 위상에 더하는 값(왼발 앞 디딤 정렬) */
  phaseOffset: number;
  /** 걷기 클립 좌·우 발목 본 최저 높이(모델 단위, 배율 1) — 디딤 고정 판정 띠를 달리기 값과 섞는다 */
  ankleMin: [number, number];
}

/** 속도 → 걷기 하체 목표 비율(0..WALK_MAX) */
export function walkTarget(speed: number): number {
  const u = Math.min(1, Math.max(0, (speed - WALK_FULL) / (RUN_FULL - WALK_FULL)));
  return WALK_MAX * (1 - u * u * (3 - 2 * u));
}

/**
 * 걷기 층 가중치. 달리기 액션 가중치를 1로 둔 채(상체가 기본 자세로 빠지지 않게) 하체만 비율을 맞춘다.
 * 하체 트랙의 다른 액션 합은 (대기 1−f) + (달리기 f) = 1 이라 three.js PropertyMixer 가 걷기 x 를 x/(1+x) 비율로 섞는다.
 * 목표 걷기 비율 c·f(달리기가 섞인 만큼만) → x = c·f/(1−c·f).
 * 예전 f·c/(1−c)는 f<1 크로스페이드에서 과대(f=0.1 → 0.76)라 정지·스킬 진입 페이드 내내 걷기 자세에 묶였다가 f≈0에서 하체가 튀었다(#371 리뷰 P1).
 */
export function walkLayerWeight(w: number, runWeight: number): number {
  if (!Number.isFinite(w) || !Number.isFinite(runWeight)) return 0;
  const share = Math.min(WALK_MAX, Math.max(0, w)) * Math.min(1, Math.max(0, runWeight));
  return share / (1 - share);
}

/**
 * 이번 프레임 달리기 실효 가중치 예측(0..1). 믹서가 섞기 가중치를 update 안에서 갱신하므로 직전 값만 쓰면 한 프레임 늦어
 * 섞기 끝 프레임에 걷기 몫이 0.06~0.32 남았다(#371 재리뷰 P2-1). 섞기는 시간에 선형이라 직전 두 값으로 선형 외삽한다.
 */
export function predictRunWeight(prev: number, prev2: number): number {
  if (!Number.isFinite(prev)) return 0;
  const p2 = Number.isFinite(prev2) ? prev2 : prev;
  return Math.min(1, Math.max(0, 2 * prev - p2));
}

/**
 * 걷기 비율 한 걸음(상태기계). entering = 달리기 클립이 아직 켜지지 않은 출발 프레임.
 * 출발: 0 에서 시작한다. 출발 첫 프레임의 보간 속도는 실제보다 낮게 읽혀(키보드 7m/s 출발이 0.6m/s 로) 그 값으로 남은 비율을
 * 쓰면 달리기 출발 몇 프레임이 걷기 하체로 섞였다(2026-11-04 turn180 재캡처 w 0.968, 발 99cm/프레임). 느린 출발은 달리기 섞기(0.1초)와 함께 오른다.
 * 이후: 줄어들 때(빨라짐) WALK_RELEASE, 늘어날 때(느려짐) 2·WALK_FOLLOW 로 따라간다.
 */
export function stepWalkW(w: number, target: number, entering: boolean, dt: number): number {
  if (!Number.isFinite(target) || !(dt > 0)) return Number.isFinite(w) ? w : 0;
  let x = Number.isFinite(w) ? w : 0;
  if (entering) x = 0;
  return x + (target - x) * (1 - Math.exp(-(target < x ? WALK_RELEASE : WALK_FOLLOW * 2) * dt));
}

/** 속도 최대 유지 프레임 수: 180° 반전·회피 직후 보간 위치가 같아 속도 0으로 읽히는 틱이 걷기 비율을 튀게 했다(#371 재리뷰 P2-2) */
export const WALK_SPEED_HOLD = 3;

/** 섞은 한 바퀴 거리로 위상을 돌리는 거리 배율(달리기 위상표 기준) */
export function walkStride(w: number, runCycleM: number, walkCycleM: number): number {
  const c = Math.min(WALK_MAX, Math.max(0, w));
  const mix = runCycleM * (1 - c) + walkCycleM * c;
  return mix > 1e-6 ? runCycleM / mix : 1;
}

/** 0..1 순환 */
export function wrap01(x: number): number { return x - Math.floor(x); }

/**
 * 로딩 때 한 번: 별도 골격 복제본에서 두 클립의 왼발 앞 디딤(발목이 가장 앞에 나온 시점)과 걷기 보폭을 잰다.
 * 실제 mixer·자세는 건드리지 않는다. 측정에 실패하면 null(걷기 층을 쓰지 않고 예전 달리기만).
 */
export function prepareWalkLayer(scene: THREE.Object3D, walk: THREE.AnimationClip, run: THREE.AnimationClip,
  runDistance: Float32Array, faceFlip: boolean, scale: number): WalkLayer | null {
  if (runDistance.length < 2 || runDistance[runDistance.length - 1] <= 0) return null;
  const tracks = walk.tracks.filter(t => LOWER.test(t.name.split('.')[0]));
  if (tracks.length < 4) return null;
  const clip = new THREE.AnimationClip('walk-lower', walk.duration, tracks);
  const copy = clone(scene);
  let lFoot: THREE.Object3D | undefined, rFoot: THREE.Object3D | undefined;
  copy.traverse(o => {
    if (/LeftFoot$/.test(o.name)) lFoot ??= o;
    else if (/RightFoot$/.test(o.name)) rFoot ??= o;
  });
  if (!lFoot || !rFoot) return null;
  const mixer = new THREE.AnimationMixer(copy);
  const p = new THREE.Vector3(), sign = faceFlip ? -1 : 1;
  const sample = (c: THREE.AnimationClip, n: number) => {
    mixer.stopAllAction();
    const a = mixer.clipAction(c).reset().setLoop(THREE.LoopOnce, 1).play();
    a.clampWhenFinished = true; a.timeScale = 0;
    const ly = new Float32Array(n + 1), lz = new Float32Array(n + 1), ry = new Float32Array(n + 1), rz = new Float32Array(n + 1);
    for (let i = 0; i <= n; i++) {
      a.time = Math.min(c.duration, c.duration * i / n);
      mixer.update(0); copy.updateMatrixWorld(true);
      lFoot!.getWorldPosition(p); ly[i] = p.y; lz[i] = p.z * sign;
      rFoot!.getWorldPosition(p); ry[i] = p.y; rz[i] = p.z * sign;
    }
    return { ly, lz, ry, rz };
  };
  const forward = (z: Float32Array): number => { let k = 0; for (let i = 1; i < z.length - 1; i++) if (z[i] > z[k]) k = i; return k; };
  try {
    const rn = runDistance.length - 1;
    const r = sample(run, rn);
    const runFrac = runDistance[forward(r.lz)] / runDistance[rn];
    const wn = Math.max(60, Math.ceil(walk.duration * 120)), dt = walk.duration / wn;
    const w = sample(walk, wn);
    const walkFrac = forward(w.lz) / wn;
    // 디딤 발(낮은 발)이 뒤로 쓸리는 속도의 중앙값 × 주기 = 한 바퀴 이동 거리(측정 기록: 1.58m, 배율 1)
    // 발목 본은 발바닥보다 높고 좌우 최저 높이가 다르다(실측 좌 0.125·우 0.114m). 발마다 최저 + STANCE_BAND 로 디딤을 고른다.
    // 예전 공통 최저 + 0.04m 는 디딤 표본이 6개뿐이라 브라우저에서 층 설치가 늘 실패했다(2026-11-04 터치 실측).
    let minL = Infinity, minR = Infinity;
    for (let i = 0; i <= wn; i++) { minL = Math.min(minL, w.ly[i]); minR = Math.min(minR, w.ry[i]); }
    const speeds: number[] = [];
    for (let i = 0; i < wn; i++) {
      const left = Math.max(w.ly[i], w.ly[i + 1]) < minL + STANCE_BAND, right = Math.max(w.ry[i], w.ry[i + 1]) < minR + STANCE_BAND;
      const v = left ? (w.lz[i] - w.lz[i + 1]) / dt : right ? (w.rz[i] - w.rz[i + 1]) / dt : 0;
      if (v > 0.3) speeds.push(v);
    }
    if (speeds.length < 8) return null;
    speeds.sort((a, b) => a - b);
    const cycleM = speeds[speeds.length >> 1] * walk.duration * scale;
    if (!(cycleM > 0.3 && cycleM < 6)) return null;
    return { clip, cycleM, phaseOffset: wrap01(walkFrac - runFrac), ankleMin: [minL, minR] };
  } finally {
    mixer.stopAllAction();
    mixer.uncacheRoot(copy);
  }
}
